import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { merchantApi } from '../api/config';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faVault, faArrowLeft, faClock, faCheck, faTimesCircle, faCheckCircle,
  faCoins, faRotate, faArrowRight, faArrowDown, faMoneyBill, faWallet, faFileInvoiceDollar,
  faPaperPlane, faInfoCircle,
} from '@fortawesome/free-solid-svg-icons';
import DataTable, { Column } from '../components/shared/DataTable';
import ActionOtpModal from '../components/shared/ActionOtpModal';
import {
  UiPageHeader, UiButton, UiModal, UiInput, UiSelect, StatusBadge, FIELD_INPUT_CLASS, FIELD_LABEL_CLASS,
} from '../components/ui';
import type { StatusDef } from '../components/ui';

interface SafeWalletTransaction {
  id: string;
  merchant_id: string;
  wallet_id: string;
  amount_thb: number;
  fee_rate: number;
  fee_amount: number;
  net_thb: number;
  usdt_rate: number;
  usdt_amount: number;
  status: 'pending' | 'approved' | 'rejected' | 'settled';
  settlement_id: string | null;
  approved_by: string | null;
  approved_at: string | null;
  rejected_reason: string | null;
  note: string | null;
  created_at: string;
  updated_at: string;
}

interface UsdtPreview {
  original_amount: number;
  fee_percent: number;
  fee_amount: number;
  net_thb: number;
  usdt_amount: number;
  rate: number;
}

/* Glass sub-stat tile for gradient (white-text) cards — matches WalletPage */
const GlassStat: React.FC<{ icon?: any; label: string; value: string }> = ({ icon, label, value }) => (
  <div className="rounded-xl bg-white/10 px-3 py-2.5 ring-1 ring-inset ring-white/15 backdrop-blur-sm">
    <p className="text-[11px] font-medium text-white/70 flex items-center gap-1.5 mb-1">
      {icon && <FontAwesomeIcon icon={icon} className="text-[10px]" />}{label}
    </p>
    <p className="text-base font-bold tracking-tight truncate" style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</p>
  </div>
);

const SafeWalletPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Wallet state
  const [wallet, setWallet] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  // Transactions state
  const [transactions, setTransactions] = useState<SafeWalletTransaction[]>([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);

  // Transfer modal state
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferAmount, setTransferAmount] = useState('');
  const [transferNote, setTransferNote] = useState('');
  const [transferring, setTransferring] = useState(false);
  const [usdtPreview, setUsdtPreview] = useState<UsdtPreview | null>(null);
  const [usdtLoading, setUsdtLoading] = useState(false);

  // Settlement modal state
  const [showSettlementModal, setShowSettlementModal] = useState(false);
  const [settlementUsdtAmount, setSettlementUsdtAmount] = useState('');
  const [settlementNetwork, setSettlementNetwork] = useState('');
  const [settlementWalletAddress, setSettlementWalletAddress] = useState('');
  const [settling, setSettling] = useState(false);
  const [minSettlementUsdt, setMinSettlementUsdt] = useState(10); // from safe_wallet_min_settlement_usdt

  // Withdraw modal state
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawNote, setWithdrawNote] = useState('');
  const [withdrawing, setWithdrawing] = useState(false);
  const [withdrawPreview, setWithdrawPreview] = useState<any>(null);
  const [withdrawPreviewLoading, setWithdrawPreviewLoading] = useState(false);

  // Action OTP
  const [showSettlementOtp, setShowSettlementOtp] = useState(false);
  const [showWithdrawOtp, setShowWithdrawOtp] = useState(false);
  const [pendingSettlementPayload, setPendingSettlementPayload] = useState<any>(null);
  const [pendingWithdrawPayload, setPendingWithdrawPayload] = useState<any>(null);

  // Load wallet data
  const loadWallet = useCallback(async () => {
    try {
      const response = await merchantApi.get('/merchants/wallet');
      setWallet(response.data.data);
    } catch (error: any) {
      toast.error(t('common.cannotLoadData'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  // Load safe wallet transactions
  const loadTransactions = useCallback(async () => {
    try {
      setLoadingTransactions(true);
      const response = await merchantApi.get('/merchants/wallet/safe-wallet/transactions');
      setTransactions(response.data.data || []);
    } catch (error) {
      console.error('Error loading safe wallet transactions');
    } finally {
      setLoadingTransactions(false);
    }
  }, []);

  // Load minimum USDT withdrawal for Safe Wallet settlement (dedicated setting, unit USDT)
  const loadSettings = useCallback(async () => {
    try {
      const response = await merchantApi.get('/admin/settings');
      const settings = response.data.data || [];
      const minSetting = settings.find((s: any) => s.setting_key === 'safe_wallet_min_settlement_usdt');
      if (minSetting) setMinSettlementUsdt(parseFloat(minSetting.setting_value) || 10);
    } catch {
      console.error('Error loading settings');
    }
  }, []);

  useEffect(() => {
    loadWallet();
    loadTransactions();
    loadSettings();
    const interval = setInterval(() => {
      loadWallet();
      loadTransactions();
    }, 30000);
    return () => clearInterval(interval);
  }, [loadWallet, loadTransactions, loadSettings]);

  // USDT rate preview with debounce
  useEffect(() => {
    if (!transferAmount || parseFloat(transferAmount) <= 0) {
      setUsdtPreview(null);
      return;
    }
    const timer = setTimeout(async () => {
      setUsdtLoading(true);
      try {
        const res = await merchantApi.get(`/merchants/wallet/usdt-rate?amount=${transferAmount}&context=safe_wallet`);
        setUsdtPreview(res.data.data.preview || null);
      } catch {
        setUsdtPreview(null);
      } finally {
        setUsdtLoading(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [transferAmount]);

  // Refresh USDT rate
  const handleRefreshRate = async () => {
    if (!transferAmount || parseFloat(transferAmount) <= 0) return;
    setUsdtLoading(true);
    try {
      const res = await merchantApi.get(`/merchants/wallet/usdt-rate?amount=${transferAmount}&context=safe_wallet`);
      setUsdtPreview(res.data.data.preview || null);
    } catch {
      setUsdtPreview(null);
    } finally {
      setUsdtLoading(false);
    }
  };

  // Handle transfer to safe wallet
  const handleTransfer = async () => {
    const amount = parseFloat(transferAmount);

    if (!amount || amount < 1000) {
      toast.error(t('safeWallet.errorMinAmount'));
      return;
    }

    if (amount > (wallet?.balance || 0)) {
      toast.error(t('safeWallet.errorInsufficientMain', { balance: (wallet?.balance || 0).toLocaleString() }));
      return;
    }

    try {
      setTransferring(true);
      const res = await merchantApi.post('/merchants/wallet/safe-wallet/transfer', {
        amount,
        note: transferNote || undefined,
      });

      if (res.data.success) {
        toast.success(res.data.message || t('safeWallet.successDeposit'));
        setShowTransferModal(false);
        setTransferAmount('');
        setTransferNote('');
        setUsdtPreview(null);
        loadWallet();
        loadTransactions();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('safeWallet.errorDepositFailed'));
    } finally {
      setTransferring(false);
    }
  };

  // Minimum USDT withdrawal check (unit USDT — direct comparison, no rate needed)
  const settlementUsdtNum = parseFloat(settlementUsdtAmount);
  const settlementBelowMin = !isNaN(settlementUsdtNum) && settlementUsdtNum > 0 && settlementUsdtNum < minSettlementUsdt;

  // Handle settlement request (ระบุ USDT อิสระ)
  const handleSettlement = () => {
    const amount = parseFloat(settlementUsdtAmount);
    if (!amount || amount <= 0) { toast.error(t('safeWallet.errorEnterUsdt')); return; }
    if (amount > (wallet?.safe_balance || 0)) { toast.error(t('safeWallet.errorInsufficientUsdt')); return; }
    if (settlementBelowMin) {
      toast.error(t('safeWallet.belowMinSettlement', { min: minSettlementUsdt.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }));
      return;
    }
    if (!settlementNetwork) { toast.error(t('safeWallet.errorSelectNetwork')); return; }
    if (!settlementWalletAddress.trim()) { toast.error(t('safeWallet.errorEnterAddress')); return; }

    setPendingSettlementPayload({
      usdt_amount: amount,
      wallet_address: settlementWalletAddress.trim(),
      network: settlementNetwork,
    });
    setShowSettlementOtp(true);
  };

  const handleSettlementVerified = async (actionToken: string) => {
    try {
      setSettling(true);
      const res = await merchantApi.post('/merchants/wallet/safe-wallet/settlement', {
        ...pendingSettlementPayload,
        action_token: actionToken,
      });
      if (res.data.success) {
        toast.success(res.data.message || t('safeWallet.successSettlement'));
        setShowSettlementModal(false);
        setSettlementUsdtAmount('');
        setSettlementNetwork('');
        setSettlementWalletAddress('');
        setPendingSettlementPayload(null);
        loadWallet();
        loadTransactions();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('safeWallet.errorSettlementFailed'));
    } finally {
      setSettling(false);
    }
  };

  // Withdraw preview with debounce (input USDT)
  useEffect(() => {
    if (!withdrawAmount || parseFloat(withdrawAmount) <= 0) {
      setWithdrawPreview(null);
      return;
    }
    const timer = setTimeout(async () => {
      setWithdrawPreviewLoading(true);
      try {
        const res = await merchantApi.get(`/merchants/wallet/safe-wallet/withdraw-preview?usdt_amount=${withdrawAmount}`);
        setWithdrawPreview(res.data.data.preview || null);
      } catch {
        setWithdrawPreview(null);
      } finally {
        setWithdrawPreviewLoading(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [withdrawAmount]);

  // Refresh withdraw rate
  const handleRefreshWithdrawRate = async () => {
    if (!withdrawAmount || parseFloat(withdrawAmount) <= 0) return;
    setWithdrawPreviewLoading(true);
    try {
      const res = await merchantApi.get(`/merchants/wallet/safe-wallet/withdraw-preview?usdt_amount=${withdrawAmount}`);
      setWithdrawPreview(res.data.data.preview || null);
    } catch {
      setWithdrawPreview(null);
    } finally {
      setWithdrawPreviewLoading(false);
    }
  };

  // Handle withdraw from safe wallet (input USDT)
  const handleWithdraw = () => {
    const amount = parseFloat(withdrawAmount);
    if (!amount || amount <= 0) { toast.error(t('safeWallet.errorEnterUsdt')); return; }
    if (amount > (wallet?.safe_balance || 0)) { toast.error(t('safeWallet.errorInsufficientSafe')); return; }

    setPendingWithdrawPayload({ usdt_amount: amount, note: withdrawNote || undefined });
    setShowWithdrawOtp(true);
  };

  const handleWithdrawVerified = async (actionToken: string) => {
    try {
      setWithdrawing(true);
      const res = await merchantApi.post('/merchants/wallet/safe-wallet/withdraw', {
        ...pendingWithdrawPayload,
        action_token: actionToken,
      });
      if (res.data.success) {
        toast.success(res.data.message || t('safeWallet.successWithdraw'));
        setShowWithdrawModal(false);
        setWithdrawAmount('');
        setWithdrawNote('');
        setWithdrawPreview(null);
        setPendingWithdrawPayload(null);
        loadWallet();
        loadTransactions();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('safeWallet.errorWithdrawFailed'));
    } finally {
      setWithdrawing(false);
    }
  };

  // Status badge map (label translated via t)
  const SAFE_STATUS_MAP: Record<string, StatusDef> = {
    pending:   { bg: 'bg-amber-100', text: 'text-amber-700', icon: faClock, label: t('safeWallet.statusPending') },
    approved:  { bg: 'bg-green-100', text: 'text-green-700', icon: faCheckCircle, label: t('safeWallet.statusApproved') },
    rejected:  { bg: 'bg-red-100', text: 'text-red-600', icon: faTimesCircle, label: t('safeWallet.statusRejected') },
    settled:   { bg: 'bg-blue-100', text: 'text-blue-700', icon: faCheck, label: t('safeWallet.statusSettled') },
    completed: { bg: 'bg-purple-100', text: 'text-purple-700', icon: faArrowDown, label: t('safeWallet.statusCompleted') },
  };

  // Table columns
  const transactionColumns: Column<SafeWalletTransaction>[] = [
    {
      key: 'icon',
      header: '',
      width: '60px',
      render: (tx) => {
        const bgMap: Record<string, string> = {
          pending: 'var(--theme-gradient-warning)',
          approved: 'var(--theme-gradient-success)',
          rejected: 'var(--theme-gradient-danger)',
          settled: 'var(--theme-gradient-info)',
          completed: 'var(--theme-accent-gradient)',
        };
        return (
          <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white" style={{ background: bgMap[tx.status] || bgMap.pending }}>
            <FontAwesomeIcon icon={faCoins} />
          </div>
        );
      },
    },
    {
      key: 'detail',
      header: t('safeWallet.colDetail'),
      render: (tx) => {
        const txType = (tx as any).transaction_type;
        const isWithdraw = txType === 'withdraw';
        const isSettlement = txType === 'settlement';
        return (
          <div>
            <p className="font-semibold text-gray-800 dark:text-slate-100">
              {isSettlement
                ? <>{t('safeWallet.txSettlement')} {tx.usdt_amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 6 })} USDT</>
                : isWithdraw
                  ? <>{t('safeWallet.txWithdraw')} {tx.usdt_amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 6 })} USDT</>
                  : <>{t('safeWallet.txDeposit')} ฿{tx.amount_thb.toLocaleString('th-TH', { minimumFractionDigits: 2 })}</>
              }
            </p>
            <div className="text-xs text-gray-500 dark:text-slate-400 mt-1 space-y-0.5">
              {isSettlement ? (
                <>
                  <div>USDT: {tx.usdt_amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 6 })} | Fee: ฿0.00</div>
                  <div>Rate: ฿{tx.usdt_rate.toLocaleString('th-TH', { minimumFractionDigits: 2 })}/USDT</div>
                </>
              ) : isWithdraw ? (
                <>
                  <div>THB: ฿{tx.amount_thb.toLocaleString('th-TH', { minimumFractionDigits: 2 })} | Fee {(tx.fee_rate * 100).toFixed(1).replace(/\.0$/, '')}%: ฿{tx.fee_amount.toLocaleString('th-TH', { minimumFractionDigits: 2 })} | <span className="text-green-600 dark:text-green-400 font-medium">Net: ฿{tx.net_thb.toLocaleString('th-TH', { minimumFractionDigits: 2 })}</span></div>
                  <div>Rate: ฿{tx.usdt_rate.toLocaleString('th-TH', { minimumFractionDigits: 2 })}/USDT</div>
                </>
              ) : (
                <>
                  <div>THB: ฿{tx.amount_thb.toLocaleString('th-TH', { minimumFractionDigits: 2 })} | Fee {(tx.fee_rate * 100).toFixed(1).replace(/\.0$/, '')}%: ฿{tx.fee_amount.toLocaleString('th-TH', { minimumFractionDigits: 2 })} | <span className="text-amber-600 dark:text-amber-400 font-medium">USDT: {tx.usdt_amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 6 })}</span></div>
                  <div>Rate: ฿{tx.usdt_rate.toLocaleString('th-TH', { minimumFractionDigits: 2 })}/USDT | Net: ฿{tx.net_thb.toLocaleString('th-TH', { minimumFractionDigits: 2 })}</div>
                </>
              )}
              <div>{new Date(tx.created_at).toLocaleString('th-TH')}</div>
            </div>
            {tx.note && <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">{tx.note}</p>}
            {tx.rejected_reason && <p className="text-xs text-red-500 dark:text-red-400 mt-1">{t('safeWallet.rejectReason')} {tx.rejected_reason}</p>}
          </div>
        );
      },
    },
    {
      key: 'status',
      header: t('safeWallet.colStatus'),
      render: (tx) => <StatusBadge status={tx.status} statusMap={SAFE_STATUS_MAP} />,
    },
    {
      key: 'type',
      header: t('safeWallet.colType'),
      width: '100px',
      render: (tx) => {
        const txType = (tx as any).transaction_type;
        const isWithdraw = txType === 'withdraw';
        const isSettlement = txType === 'settlement';
        const className = isSettlement
          ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
          : isWithdraw
            ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400'
            : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400';
        const label = isSettlement
          ? t('safeWallet.typeSettlement')
          : isWithdraw
            ? t('safeWallet.typeWithdraw')
            : t('safeWallet.typeDeposit');
        return (
          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${className}`}>
            {label}
          </span>
        );
      },
    },
  ];

  // Main wallet available balance
  const mainBalance = wallet?.balance || 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <UiPageHeader
        icon={faVault}
        title={t('safeWallet.pageTitle')}
        subtitle={t('safeWallet.pageSubtitle')}
        actions={
          <UiButton icon={faArrowLeft} onClick={() => navigate('/wallet')}>
            {t('safeWallet.backToWallet')}
          </UiButton>
        }
      />

      {/* Balance Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
        {/* Safe Wallet Card */}
        <div className="relative overflow-hidden rounded-3xl p-6 text-white shadow-lg ring-1 ring-white/10 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl"
          style={{ background: 'var(--theme-secondary-gradient)' }}>
          <div className="pointer-events-none absolute -top-14 -right-10 h-40 w-40 rounded-full bg-white/20 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-16 -left-10 h-36 w-36 rounded-full bg-black/10 blur-3xl" />
          <div className="relative">
            <div className="flex flex-col gap-3 mb-5">
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-11 w-11 rounded-2xl bg-white/15 ring-1 ring-white/25 backdrop-blur flex items-center justify-center text-lg shadow-inner shrink-0">
                  <FontAwesomeIcon icon={faVault} />
                </div>
                <div className="min-w-0">
                  <h2 className="font-bold text-[15px] leading-tight">Safe Wallet</h2>
                  <p className="text-[11px] text-white/65 truncate">{t('safeWallet.safeWalletCardSubtitle')}</p>
                </div>
              </div>
              <div className="flex gap-1.5 flex-wrap">
                <button
                  onClick={() => setShowSettlementModal(true)}
                  disabled={!(wallet?.safe_balance > 0)}
                  className="px-3 py-2 rounded-xl text-xs font-semibold flex items-center gap-1.5 bg-white/15 hover:bg-white/25 ring-1 ring-white/25 backdrop-blur transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <FontAwesomeIcon icon={faPaperPlane} className="text-[11px]" />
                  {t('safeWallet.withdrawUSDT')}
                </button>
                <button
                  onClick={() => setShowWithdrawModal(true)}
                  disabled={!(wallet?.safe_balance > 0)}
                  className="px-3 py-2 rounded-xl text-xs font-semibold flex items-center gap-1.5 bg-black/15 hover:bg-black/25 ring-1 ring-white/20 backdrop-blur transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <FontAwesomeIcon icon={faArrowDown} className="text-[11px]" />
                  {t('safeWallet.withdrawToMain')}
                </button>
                <button
                  onClick={() => setShowTransferModal(true)}
                  className="px-3 py-2 rounded-xl text-xs font-semibold flex items-center gap-1.5 bg-white/15 hover:bg-white/25 ring-1 ring-white/25 backdrop-blur transition-colors"
                >
                  <FontAwesomeIcon icon={faArrowRight} className="text-[11px]" />
                  {t('safeWallet.depositToSafe')}
                </button>
              </div>
            </div>

            <p className="text-[11px] font-medium uppercase tracking-wider text-white/65">USDT Balance</p>
            <p className="mt-1 text-[28px] leading-none font-black tracking-tight" style={{ fontVariantNumeric: 'tabular-nums' }}>
              {(wallet?.safe_balance || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 6 })}
              <span className="text-base font-bold text-white/70 ml-1.5">USDT</span>
            </p>

            <div className="grid grid-cols-2 gap-3 mt-5">
              <GlassStat icon={faMoneyBill} label={t('safeWallet.totalDeposited')} value={`${(wallet?.total_safe_credit || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 6 })} USDT`} />
              <GlassStat icon={faWallet} label={t('safeWallet.totalWithdrawn')} value={`${(wallet?.total_safe_debit || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 6 })} USDT`} />
            </div>
          </div>
        </div>

        {/* Main Wallet Quick View */}
        <div className="relative overflow-hidden rounded-3xl p-6 bg-white dark:bg-slate-800 ring-1 ring-gray-100 dark:ring-slate-700 shadow-lg transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl">
          <div className="pointer-events-none absolute -top-16 -right-12 h-40 w-40 rounded-full blur-3xl opacity-60" style={{ background: 'var(--theme-primary-light)' }} />
          <div className="relative">
            <div className="flex items-center gap-3 mb-5">
              <div className="h-11 w-11 rounded-2xl bg-primary/10 text-primary ring-1 ring-primary/15 flex items-center justify-center text-lg">
                <FontAwesomeIcon icon={faWallet} />
              </div>
              <div className="min-w-0">
                <h2 className="font-bold text-gray-800 dark:text-slate-100 text-[15px] leading-tight">Main Wallet</h2>
                <p className="text-[11px] text-gray-400 dark:text-slate-500 truncate">{t('safeWallet.mainWalletSubtitle')}</p>
              </div>
            </div>

            <p className="text-[11px] font-medium uppercase tracking-wider text-gray-400 dark:text-slate-500">{t('wallet.balance')}</p>
            <p className="mt-1 text-[28px] leading-none font-black tracking-tight text-gray-800 dark:text-slate-100" style={{ fontVariantNumeric: 'tabular-nums' }}>
              ฿{mainBalance.toLocaleString('th-TH', { minimumFractionDigits: 2 })}
            </p>

            <div className="grid grid-cols-2 gap-3 mt-5">
              <div className="rounded-xl bg-green-50 dark:bg-green-900/20 ring-1 ring-inset ring-green-100 dark:ring-green-900/30 px-3 py-2.5">
                <p className="text-[11px] font-medium text-green-600 dark:text-green-400 flex items-center gap-1.5 mb-1"><FontAwesomeIcon icon={faMoneyBill} className="text-[10px]" /> {t('wallet.income')}</p>
                <p className="text-base font-bold text-green-700 dark:text-green-300 tracking-tight truncate" style={{ fontVariantNumeric: 'tabular-nums' }}>฿{(wallet?.total_credit || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })}</p>
              </div>
              <div className="rounded-xl bg-red-50 dark:bg-red-900/20 ring-1 ring-inset ring-red-100 dark:ring-red-900/30 px-3 py-2.5">
                <p className="text-[11px] font-medium text-red-600 dark:text-red-400 flex items-center gap-1.5 mb-1"><FontAwesomeIcon icon={faWallet} className="text-[10px]" /> {t('wallet.expense')}</p>
                <p className="text-base font-bold text-red-700 dark:text-red-300 tracking-tight truncate" style={{ fontVariantNumeric: 'tabular-nums' }}>฿{(wallet?.total_debit || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Transactions Table */}
      <DataTable<SafeWalletTransaction>
        data={transactions}
        columns={transactionColumns}
        loading={loadingTransactions}
        emptyMessage={t('safeWallet.noTransactions')}
        emptyIcon={<FontAwesomeIcon icon={faFileInvoiceDollar} className="text-5xl text-gray-300 dark:text-slate-600" />}
        keyExtractor={(tx) => tx.id}
        pagination={{
          enabled: true,
          pageSize: 10,
          pageSizeOptions: [5, 10, 20],
          showPageSizeSelector: true,
          showInfo: true,
          showFirstLast: false,
        }}
        headerContent={
          <h2 className="text-lg font-bold text-gray-800 dark:text-slate-100 flex items-center gap-2">
            <FontAwesomeIcon icon={faCoins} className="text-amber-500" />
            {t('safeWallet.transactionHistory')}
          </h2>
        }
        hoverable
      />

      {/* Transfer (Deposit to Safe) Modal */}
      <UiModal
        open={showTransferModal}
        onClose={() => setShowTransferModal(false)}
        title={t('safeWallet.depositModalTitle')}
        icon={faVault}
        iconColor="text-amber-500"
        size="md"
        scrollable
        footer={
          <div className="flex gap-3">
            <UiButton variant="secondary" onClick={() => setShowTransferModal(false)} disabled={transferring} className="flex-1 justify-center">
              {t('common.cancel')}
            </UiButton>
            <UiButton
              onClick={handleTransfer}
              loading={transferring}
              disabled={!transferAmount || parseFloat(transferAmount) < 1000}
              className="flex-1 justify-center"
            >
              {transferring ? t('safeWallet.depositing') : t('safeWallet.confirmDeposit')}
            </UiButton>
          </div>
        }
      >
        <p className="text-sm text-gray-500 dark:text-slate-400">{t('safeWallet.depositModalDesc')}</p>

        {/* Balance display */}
        <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-4 flex items-center justify-between">
          <div>
            <p className="text-xs text-gray-500 dark:text-slate-400">Main Wallet</p>
            <p className="text-lg font-bold text-gray-800 dark:text-slate-100">฿{mainBalance.toLocaleString()}</p>
          </div>
          <FontAwesomeIcon icon={faArrowRight} className="text-amber-500 text-xl" />
          <div className="text-right">
            <p className="text-xs text-gray-500 dark:text-slate-400">Safe Wallet</p>
            <p className="text-lg font-bold text-amber-600 dark:text-amber-400">{(wallet?.safe_balance || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })} USDT</p>
          </div>
        </div>

        {/* Amount input */}
        <div>
          <UiInput
            label={t('safeWallet.amountLabel')}
            prefix="฿"
            type="number"
            step="0.01"
            min="1000"
            max={mainBalance}
            value={transferAmount}
            onChange={(e) => setTransferAmount(e.target.value)}
            placeholder="0.00"
            className="text-lg font-semibold"
          />
          <div className="flex gap-2 mt-2">
            {[25, 50, 75].map(pct => (
              <button
                key={pct}
                type="button"
                onClick={() => setTransferAmount(String(Math.floor(mainBalance * pct / 100)))}
                className="px-3 py-1.5 bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 text-gray-700 dark:text-slate-200 rounded-lg text-xs font-medium transition-colors"
              >
                {pct}%
              </button>
            ))}
            <button
              type="button"
              onClick={() => setTransferAmount(String(mainBalance))}
              className="px-3 py-1.5 bg-amber-100 dark:bg-amber-900/30 hover:bg-amber-200 dark:hover:bg-amber-900/50 text-amber-700 dark:text-amber-400 rounded-lg text-xs font-medium transition-colors"
            >
              {t('safeWallet.allAmount')}
            </button>
          </div>
        </div>

        {/* USDT Preview */}
        <div className="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 flex items-center gap-1">
              <FontAwesomeIcon icon={faCoins} /> {t('safeWallet.usdtPreviewTitle')}
            </p>
            <button
              type="button"
              onClick={handleRefreshRate}
              disabled={usdtLoading || !transferAmount || parseFloat(transferAmount) <= 0}
              className="flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400 hover:text-amber-800 dark:hover:text-amber-300 disabled:opacity-40 transition-colors"
            >
              <FontAwesomeIcon icon={faRotate} className={usdtLoading ? 'animate-spin' : ''} />
              {t('safeWallet.refreshRate')}
            </button>
          </div>
          {usdtLoading ? (
            <p className="text-xs text-gray-400 dark:text-slate-500">{t('safeWallet.loadingRate')}</p>
          ) : usdtPreview ? (
            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-slate-400">{t('safeWallet.depositAmountLabel')}</span>
                <span className="font-medium text-gray-800 dark:text-slate-100">฿{usdtPreview.original_amount.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between text-red-600 dark:text-red-400">
                <span>Fee {usdtPreview.fee_percent}%</span>
                <span className="font-medium">-฿{usdtPreview.fee_amount.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between border-t border-amber-200 dark:border-amber-800 pt-1.5">
                <span className="text-gray-600 dark:text-slate-300">Net THB</span>
                <span className="font-semibold text-gray-800 dark:text-slate-100">฿{usdtPreview.net_thb.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between text-xs text-gray-400 dark:text-slate-500">
                <span>Exchange Rate</span>
                <span>1 USDT = ฿{usdtPreview.rate.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between mt-1 bg-white dark:bg-slate-800 rounded-lg px-3 py-2 border border-amber-300 dark:border-amber-800">
                <span className="font-semibold text-amber-700 dark:text-amber-400">{t('safeWallet.usdtToReceive')}</span>
                <span className="font-bold text-amber-700 dark:text-amber-400 text-base">{usdtPreview.usdt_amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 6 })} USDT</span>
              </div>
            </div>
          ) : (
            <p className="text-xs text-gray-400 dark:text-slate-500">{t('safeWallet.enterAmountForPreview')}</p>
          )}
        </div>

        {/* Note input */}
        <UiInput
          label={t('safeWallet.noteLabel')}
          type="text"
          value={transferNote}
          onChange={(e) => setTransferNote(e.target.value)}
          placeholder={t('safeWallet.depositNotePlaceholder')}
          maxLength={200}
        />
      </UiModal>

      {/* Withdraw (to Main) Modal */}
      <UiModal
        open={showWithdrawModal}
        onClose={() => setShowWithdrawModal(false)}
        title={t('safeWallet.withdrawModalTitle')}
        icon={faArrowDown}
        iconColor="text-purple-500"
        size="md"
        scrollable
        footer={
          <div className="flex gap-3">
            <UiButton variant="secondary" onClick={() => setShowWithdrawModal(false)} disabled={withdrawing} className="flex-1 justify-center">
              {t('common.cancel')}
            </UiButton>
            <UiButton
              onClick={handleWithdraw}
              loading={withdrawing}
              disabled={!withdrawAmount || parseFloat(withdrawAmount) <= 0 || parseFloat(withdrawAmount) > (wallet?.safe_balance || 0)}
              className="flex-1 justify-center"
            >
              {withdrawing ? t('safeWallet.withdrawing') : t('safeWallet.confirmWithdraw')}
            </UiButton>
          </div>
        }
      >
        <p className="text-sm text-gray-500 dark:text-slate-400">{t('safeWallet.withdrawModalDesc')}</p>

        {/* Balance display */}
        <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-4 flex items-center justify-between">
          <div>
            <p className="text-xs text-gray-500 dark:text-slate-400">Safe Wallet</p>
            <p className="text-lg font-bold text-amber-600 dark:text-amber-400">{(wallet?.safe_balance || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 6 })} USDT</p>
          </div>
          <FontAwesomeIcon icon={faArrowRight} className="text-purple-500 text-xl" />
          <div className="text-right">
            <p className="text-xs text-gray-500 dark:text-slate-400">Main Wallet</p>
            <p className="text-lg font-bold text-gray-800 dark:text-slate-100">฿{mainBalance.toLocaleString()}</p>
          </div>
        </div>

        {/* USDT Amount input */}
        <div>
          <label className={FIELD_LABEL_CLASS}>{t('safeWallet.withdrawUsdtLabel')}</label>
          <div className="relative">
            <input
              type="number"
              step="0.01"
              min="0.01"
              max={wallet?.safe_balance || 0}
              value={withdrawAmount}
              onChange={(e) => setWithdrawAmount(e.target.value)}
              placeholder="0.00"
              className={`${FIELD_INPUT_CLASS} pr-16 text-lg font-semibold`}
            />
            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500 font-medium">USDT</span>
          </div>
          <div className="flex gap-2 mt-2">
            {[25, 50, 75].map(pct => (
              <button
                key={pct}
                type="button"
                onClick={() => setWithdrawAmount(String(parseFloat(((wallet?.safe_balance || 0) * pct / 100).toFixed(6))))}
                className="px-3 py-1.5 bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 text-gray-700 dark:text-slate-200 rounded-lg text-xs font-medium transition-colors"
              >
                {pct}%
              </button>
            ))}
            <button
              type="button"
              onClick={() => setWithdrawAmount(String(wallet?.safe_balance || 0))}
              className="px-3 py-1.5 bg-purple-100 dark:bg-purple-900/30 hover:bg-purple-200 dark:hover:bg-purple-900/50 text-purple-700 dark:text-purple-400 rounded-lg text-xs font-medium transition-colors"
            >
              {t('safeWallet.allAmount')}
            </button>
          </div>
        </div>

        {/* Withdraw Preview */}
        <div className="rounded-xl border border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/20 p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-purple-700 dark:text-purple-400 flex items-center gap-1">
              <FontAwesomeIcon icon={faCoins} /> {t('safeWallet.withdrawPreviewTitle')}
            </p>
            <button
              type="button"
              onClick={handleRefreshWithdrawRate}
              disabled={withdrawPreviewLoading || !withdrawAmount || parseFloat(withdrawAmount) <= 0}
              className="flex items-center gap-1 text-xs text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300 disabled:opacity-40 transition-colors"
            >
              <FontAwesomeIcon icon={faRotate} className={withdrawPreviewLoading ? 'animate-spin' : ''} />
              {t('safeWallet.refreshRate')}
            </button>
          </div>
          {withdrawPreviewLoading ? (
            <p className="text-xs text-gray-400 dark:text-slate-500">{t('safeWallet.loadingRate')}</p>
          ) : withdrawPreview ? (
            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-slate-400">{t('safeWallet.usdtWithdrawn')}</span>
                <span className="font-medium text-gray-800 dark:text-slate-100">{withdrawPreview.usdt_amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 6 })} USDT</span>
              </div>
              <div className="flex justify-between text-xs text-gray-400 dark:text-slate-500">
                <span>Exchange Rate</span>
                <span>1 USDT = ฿{withdrawPreview.rate.toLocaleString('th-TH', { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-slate-400">{t('safeWallet.thbValue')}</span>
                <span className="font-medium text-gray-800 dark:text-slate-100">฿{withdrawPreview.thb_amount.toLocaleString('th-TH', { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between text-red-600 dark:text-red-400">
                <span>Fee {withdrawPreview.fee_percent}%</span>
                <span className="font-medium">-฿{withdrawPreview.fee_amount.toLocaleString('th-TH', { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between mt-1 bg-white dark:bg-slate-800 rounded-lg px-3 py-2 border border-purple-300 dark:border-purple-800">
                <span className="font-semibold text-purple-700 dark:text-purple-400">{t('safeWallet.thbToReceive')}</span>
                <span className="font-bold text-purple-700 dark:text-purple-400 text-base">฿{withdrawPreview.net_thb.toLocaleString('th-TH', { minimumFractionDigits: 2 })}</span>
              </div>
            </div>
          ) : (
            <p className="text-xs text-gray-400 dark:text-slate-500">{t('safeWallet.enterUsdtForPreview')}</p>
          )}
        </div>

        {/* Note input */}
        <UiInput
          label={t('safeWallet.noteLabel')}
          type="text"
          value={withdrawNote}
          onChange={(e) => setWithdrawNote(e.target.value)}
          placeholder={t('safeWallet.withdrawNotePlaceholder')}
          maxLength={200}
        />
      </UiModal>

      {/* Settlement (Withdraw USDT external) Modal */}
      <UiModal
        open={showSettlementModal}
        onClose={() => setShowSettlementModal(false)}
        title={t('safeWallet.settlementModalTitle')}
        icon={faPaperPlane}
        iconColor="text-blue-500"
        size="md"
        scrollable
        footer={
          <div className="flex gap-3">
            <UiButton variant="secondary" onClick={() => setShowSettlementModal(false)} disabled={settling} className="flex-1 justify-center">
              {t('common.cancel')}
            </UiButton>
            <UiButton
              onClick={handleSettlement}
              loading={settling}
              disabled={!settlementUsdtAmount || parseFloat(settlementUsdtAmount) <= 0 || parseFloat(settlementUsdtAmount) > (wallet?.safe_balance || 0) || settlementBelowMin || !settlementNetwork || !settlementWalletAddress.trim()}
              className="flex-1 justify-center"
            >
              {settling ? t('safeWallet.settling') : t('safeWallet.confirmSettlement')}
            </UiButton>
          </div>
        }
      >
        <p className="text-sm text-gray-500 dark:text-slate-400">{t('safeWallet.settlementModalDesc')}</p>

        {/* Safe balance display */}
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 border border-blue-100 dark:border-blue-800 text-center">
          <p className="text-sm text-blue-600 dark:text-blue-400 mb-1">{t('safeWallet.safeBalanceLabel')}</p>
          <p className="text-3xl font-bold text-blue-700 dark:text-blue-300">
            {(wallet?.safe_balance || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 6 })} USDT
          </p>
        </div>

        {/* No additional fee notice */}
        <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-3 border border-green-100 dark:border-green-800 flex items-start gap-2">
          <FontAwesomeIcon icon={faInfoCircle} className="text-green-500 mt-0.5" />
          <p className="text-xs text-green-700 dark:text-green-400">{t('safeWallet.noFeeNotice')}</p>
        </div>

        {/* USDT amount input */}
        <div>
          <label className={FIELD_LABEL_CLASS}>{t('safeWallet.settlementUsdtLabel')}</label>
          <div className="relative">
            <input
              type="number"
              step="0.01"
              min="0.01"
              max={wallet?.safe_balance || 0}
              value={settlementUsdtAmount}
              onChange={(e) => setSettlementUsdtAmount(e.target.value)}
              placeholder="0.00"
              className={`${FIELD_INPUT_CLASS} pr-16 text-lg font-semibold`}
            />
            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500 font-medium">USDT</span>
          </div>
          <div className="flex gap-2 mt-2">
            {[25, 50, 75].map(pct => (
              <button
                key={pct}
                type="button"
                onClick={() => setSettlementUsdtAmount(String(parseFloat(((wallet?.safe_balance || 0) * pct / 100).toFixed(6))))}
                className="px-3 py-1.5 bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 text-gray-700 dark:text-slate-200 rounded-lg text-xs font-medium transition-colors"
              >
                {pct}%
              </button>
            ))}
            <button
              type="button"
              onClick={() => setSettlementUsdtAmount(String(wallet?.safe_balance || 0))}
              className="px-3 py-1.5 bg-blue-100 dark:bg-blue-900/30 hover:bg-blue-200 dark:hover:bg-blue-900/50 text-blue-700 dark:text-blue-400 rounded-lg text-xs font-medium transition-colors"
            >
              {t('safeWallet.allAmount')}
            </button>
          </div>
          {/* Minimum withdrawal hint (from safe_wallet_min_settlement_usdt, unit USDT) */}
          <p className="mt-2 text-xs text-gray-400 dark:text-slate-500">
            {t('safeWallet.settlementMinHint', {
              min: minSettlementUsdt.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
            })}
          </p>
          {settlementBelowMin && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {t('safeWallet.belowMinSettlement', { min: minSettlementUsdt.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) })}
            </p>
          )}
        </div>

        {/* Network select */}
        <UiSelect
          label="Network"
          icon={faCoins}
          required
          value={settlementNetwork}
          onChange={(e) => setSettlementNetwork(e.target.value)}
        >
          <option value="">{t('safeWallet.selectNetwork')}</option>
          <option value="TRC20">TRC20 (Tron)</option>
          <option value="ERC20">ERC20 (Ethereum)</option>
          <option value="BEP20">BEP20 (BSC)</option>
        </UiSelect>

        {/* Wallet address input */}
        <UiInput
          label="Wallet Address"
          required
          type="text"
          value={settlementWalletAddress}
          onChange={(e) => setSettlementWalletAddress(e.target.value)}
          placeholder="e.g. TXxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
          className="font-mono text-sm"
        />
      </UiModal>

      <ActionOtpModal
        isOpen={showSettlementOtp}
        onClose={() => setShowSettlementOtp(false)}
        onVerified={handleSettlementVerified}
        action="safe_wallet_settlement"
        title="ยืนยันการเบิก USDT"
        description={`เบิก ${pendingSettlementPayload?.usdt_amount} USDT ไปยัง ${pendingSettlementPayload?.network}`}
      />

      <ActionOtpModal
        isOpen={showWithdrawOtp}
        onClose={() => setShowWithdrawOtp(false)}
        onVerified={handleWithdrawVerified}
        action="safe_wallet_withdraw"
        title="ยืนยันการถอนกลับ Main"
        description={`ถอน ${pendingWithdrawPayload?.usdt_amount} USDT กลับ Main Wallet`}
      />
    </div>
  );
};

export default SafeWalletPage;
